import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MarketingFooter from '@/components/landing/MarketingFooter';
import MarketingNav from '@/components/landing/MarketingNav';
import LaunchFaqSection from '@/components/launch/launch-faq-section';
import LaunchFinalCtaSection from '@/components/launch/launch-final-cta-section';
import LaunchFoundersSection from '@/components/launch/launch-founders-section';
import LaunchHeroSection from '@/components/launch/launch-hero-section';
import LaunchOutcomesSection from '@/components/launch/launch-outcomes-section';
import LaunchPricingSection from '@/components/launch/launch-pricing-section';
import LaunchProblemSection from '@/components/launch/launch-problem-section';
import LaunchReportSection from '@/components/launch/launch-report-section';
import LaunchSolutionSection from '@/components/launch/launch-solution-section';
import LaunchVisionSection from '@/components/launch/launch-vision-section';
import SEOHead from '@/components/seo/SEOHead';
import { toast } from '@/hooks/use-toast';
import { FAQ_JSONLD, WEB_APPLICATION_JSONLD } from '@/lib/seoSchemas';
import { getCurrentSession, invokeEdgeFunction } from '@/lib/supabaseAdapter';
import { addWaitlistSignup } from '@/services/waitlistService';
import { useInView } from './launch/helpers';

const Launch = () => {
  const navigate = useNavigate();

  useEffect(() => {
    getCurrentSession().then((session) => {
      if (session) navigate('/dashboard', { replace: true });
    });
  }, [navigate]);

  const [heroEmail, setHeroEmail] = useState('');
  const [heroSubmitted, setHeroSubmitted] = useState(false);
  const [heroLoading, setHeroLoading] = useState(false);
  const [footerEmail, setFooterEmail] = useState('');
  const [footerSubmitted, setFooterSubmitted] = useState(false);
  const [footerLoading, setFooterLoading] = useState(false);
  const [reportEmail, setReportEmail] = useState('');
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  const submitWaitlist = useCallback(
    async (
      email: string,
      source: string,
      setLoading: (b: boolean) => void,
      setDone: (b: boolean) => void,
    ) => {
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast({ title: 'Please enter a valid email', variant: 'destructive' });
        return;
      }
      setLoading(true);
      try {
        await addWaitlistSignup(email, source);
        setDone(true);
        invokeEdgeFunction('lead-notify', {
          action: 'waitlist',
          email,
          source,
        });
      } catch (err) {
        toast({
          title: 'Something went wrong',
          description: err instanceof Error ? err.message : 'Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const s2 = useInView();
  const s3 = useInView();
  const s5 = useInView();
  const s6 = useInView();
  const s7 = useInView();
  const s10 = useInView();
  const s11 = useInView();
  const s12 = useInView();

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  return (
    <>
      <SEOHead
        title="VenueIntel — Decision Intelligence for Nightclubs & Music Venues"
        description="VenueIntel connects your POS, ticketing, CRM, and marketing into one intelligence layer — turning fragmented data into revenue-driving decisions for nightclubs and music venues."
        canonical="https://venueintel.xyz/"
        jsonLd={[WEB_APPLICATION_JSONLD, FAQ_JSONLD]}
      />
      <div
        className="min-h-screen bg-background text-foreground font-sans"
        style={{ scrollBehavior: 'smooth' }}
      >
        <MarketingNav />

        <LaunchHeroSection
          heroEmail={heroEmail}
          setHeroEmail={setHeroEmail}
          heroSubmitted={heroSubmitted}
          heroLoading={heroLoading}
          onSubmitHero={() =>
            submitWaitlist(heroEmail, 'launch-hero', setHeroLoading, setHeroSubmitted)
          }
          scrollTo={scrollTo}
        />

        <LaunchProblemSection sectionRef={s2.ref} visible={s2.visible} />

        <LaunchSolutionSection sectionRef={s3.ref} visible={s3.visible} />

        <LaunchVisionSection />

        <LaunchOutcomesSection sectionRef={s5.ref} visible={s5.visible} />

        <LaunchFoundersSection sectionRef={s6.ref} visible={s6.visible} />

        <LaunchReportSection
          sectionRef={s11.ref}
          visible={s11.visible}
          reportEmail={reportEmail}
          setReportEmail={setReportEmail}
          reportSubmitted={reportSubmitted}
          reportLoading={reportLoading}
          onSubmitReport={() =>
            submitWaitlist(reportEmail, 'report-download', setReportLoading, setReportSubmitted)
          }
        />

        {false && (
          <LaunchPricingSection sectionRef={s7.ref} visible={s7.visible} scrollTo={scrollTo} />
        )}

        <LaunchFaqSection sectionRef={s12.ref} visible={s12.visible} />

        <LaunchFinalCtaSection
          sectionRef={s10.ref}
          visible={s10.visible}
          footerEmail={footerEmail}
          setFooterEmail={setFooterEmail}
          footerSubmitted={footerSubmitted}
          footerLoading={footerLoading}
          onSubmitFooter={() =>
            submitWaitlist(footerEmail, 'launch-footer', setFooterLoading, setFooterSubmitted)
          }
        />

        <MarketingFooter />
      </div>
    </>
  );
};

export default Launch;
